import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';

export interface CurrentTenant {
  id: string;
  name: string;
  /** R-146 — per-tenant template for batch primary label. Null = use `displayId`. */
  batchDisplayTemplate?: string | null;
  /** R-146 — per-tenant template for document primary label. */
  documentDisplayTemplate?: string | null;
  /** R-146 — per-tenant template for page primary label. */
  pageDisplayTemplate?: string | null;
  /**
   * R-155 — tenant-wide default for the auto-export gate. When true, every
   * batch in this tenant requires a manual "Release for Export" click on
   * export-ready; when false (default), batches whose job type has active
   * export destinations fire automatically. Per-job-type overrides live on
   * JobType.requireManualExportRelease (null = inherit this value).
   * Optional because pre-R-155 API responses don't include it.
   */
  requireManualExportRelease?: boolean;
  /**
   * R-167 — channel → source-code map (e.g. {upload: "UPL", scanner: "SCN"}).
   * Renderer reads this when resolving the {sourceCode} token at write time.
   * Optional because pre-R-167 API responses don't include it.
   */
  channelSourceCodes?: Record<string, string> | null;
  /**
   * R-167 — scanner device-name → code map. Optional override of the
   * scanner channel code, keyed by the device/agent name as reported by the
   * delivering channel (Pull Agent or hot folder — Web TWAIN descoped v1).
   */
  scannerDeviceCodes?: Record<string, string> | null;
  /**
   * F-065 — tenant opt-in for the read-only operator automation guide
   * (`/rules-guide`). Default false: the guide is admin-only. Never widens
   * access to job configuration. Optional because pre-F-065 API responses
   * don't include it.
   */
  operatorVisibleRules?: boolean;
}

export function useCurrentTenant(): { data: CurrentTenant | null; loading: boolean } {
  const [data, setData] = useState<CurrentTenant | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // Use .then(onFulfilled, onRejected) to attach the rejection handler
    // synchronously — avoids the one-microtask window where `await` leaves
    // the rejection technically unhandled (which triggers vitest's global
    // unhandledRejection listener in multi-test files).
    api.get<CurrentTenant>('/me/tenant').then(
      (t) => {
        if (!cancelled) {
          setData(t);
          setLoading(false);
        }
      },
      () => {
        if (!cancelled) {
          setData({ id: '', name: 'Tenant' });
          setLoading(false);
        }
      },
    );

    return () => { cancelled = true; };
  }, []);

  return { data, loading };
}
